<template>
    <div :class="['step-wrapper', isFullHeightPage ? 'full-height' : 'footer-shift']">
        <BackButton />

        <div class="d-lg-none">
            <StepProgressIndicator :compact="true" />
        </div>

        <div class="container d-lg-none">
            <div class="row justify-content-center">
                <div class="col-10 col-md-8">
                    <ProgressBar />
                </div>
            </div>
        </div>

        <div class="container" v-if="title">
            <div class="row">
                <div class="col">
                    <h2 class="wizard-title">
                        {{ title }}
                    </h2>
                </div>
            </div>
        </div>

        <div class="container" v-if="!loading">
            <form @submit="submit">
                <div class="row">
                    <div class="col">
                        <div :class="['slot', layoutType]">
                            <div class="row" v-if="subText">
                                <div class="col sub-heading" v-html="subText"></div>
                            </div>

                            <div :class="{ 'form-container': tooltipType === 'small' }">
                                <div class="row" v-if="customTooltip && customInfoBlocksEnabled">
                                    <div class="col">
                                        <CustomInfoBlock :step="store.state.apply.currentRouteStep.name" />
                                    </div>
                                </div>

                                <div
                                    class="row"
                                    v-if="(tooltip && !customInfoBlocksEnabled) || displayTooltipWithCustomInfoBlock"
                                >
                                    <div class="col">
                                        <InfoBlock>
                                            <div v-html="tooltip"></div>
                                        </InfoBlock>
                                    </div>
                                </div>
                            </div>
                            <router-view />
                        </div>

                        <div class="input-wrapper error-wrapper">
                            <div class="error" v-if="errorMessage">{{ errorMessage }}</div>
                        </div>

                        <button
                            class="btn button continue-button"
                            id="continue-button"
                            :disabled="busy"
                            v-if="!hideContinue"
                        >
                            {{ continueText }}

                            <div class="spinner-border spinner-border-sm text-light ms-1" role="status" v-show="busy">
                                <span class="sr-only">{{ $t('form.general.loading') }}...</span>
                            </div>
                        </button>

                        <div v-if="showLegal" class="legal" :class="['slot', layoutType]">
                            <div v-html="legalNotice"></div>
                        </div>

                        <div v-if="isLoanDetailsAmountPage">
                            <div class="row justify-content-center" v-if="additionalInfo">
                                <div class="slot large">
                                    <div class="col-md-12">
                                        <InfoBlock :hideIcon="true" :additionalInfoHeading="additionalInfoHeading">
                                            <div v-html="additionalInfo"></div>
                                        </InfoBlock>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>

    <PrimaryFooter :showLegalFooter="!showLegal" />
</template>

<script>
import { errorNotify } from '@js/utilities/bugsnag.js'
import { submit as submitApplication } from '@config/wizardSubmit.js'
import { useForm } from 'vee-validate'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'

// lodash components
import forEach from 'lodash/forEach'
import indexOf from 'lodash/indexOf'
import isEmpty from 'lodash/isEmpty'

export default {
    data() {
        return {
            step: null,
            busy: false,
            loading: true,
            fieldErrors: {},
            errorMessage: null,
            forcedUpdate: false,
            subTextObjects: {},
            title: null,
            tooltipText: null,
            timedOut: false,
        }
    },
    setup() {
        const { validate } = useForm()
        const router = useRouter()
        const route = useRoute()
        const store = useStore()

        return {
            validate,
            router,
            route,
            store,
        }
    },
    created() {
        this.store.dispatch('setCurrentRouteStep', this.route)

        this.bindEvents()

        this.trackEvent('form_load', {
            journeyID: this.store.state.apply.journey?.id,
            sectionTitle: this.store.getters.journeyInfo.currentSection?.name,
            stepName: this.store.getters.stepViaRouteName(this.route.name)?.key,
            territory: process.env.TERRITORY,
        })

        this.loading = false
    },
    beforeMount() {
        this.emitter.on('updateSubText', payload => {
            if (!isEmpty(payload.termMetaObjects)) {
                this.subTextObjects = payload.termMetaObjects
            }

            this.store.commit('updateMetaData', {
                route: payload.route,
                meta: payload.meta,
                value: payload.value,
            })

            this.forcedUpdate = true
        })
    },
    async mounted() {
        const queryParams = this.$route.query
        if ('partner_credit_score' in queryParams) {
            this.store.state.apply.data.partnerCreditScore.value = queryParams.partner_credit_score
        }

        this.timeoutCheck()
    },
    methods: {
        timeoutCheck() {
            setTimeout(async () => {
                try {
                    const response = await axios({
                        method: 'POST',
                        url: this.url('/api/v2/session-status'),
                    })

                    if (response?.data?.status !== 'ok') {
                        this.timedOut = true
                    }
                } catch (error) {
                    if (error?.response?.status === 419) {
                        this.timedOut = true
                    }
                }

                // run again
                if (!this.timedOut) {
                    this.timeoutCheck()
                } else {
                    // TODO: maybe show a message at the top of the page
                    console.error('Session timed out')
                }
            }, window.TIMEOUT_CHECK)
        },
        submit: async function (e) {
            document.activeElement.blur()
            if (e) {
                e.preventDefault()
            }

            this.busy = true
            this.errorMessage = null

            const ignoreValidation = ['apply.summary']

            this.trimSpacers()

            const { valid } = await this.validate()

            if (indexOf(ignoreValidation, this.route.name) >= 0 || valid) {
                if (this.willSubmitResults) {
                    this.submitForm()
                } else {
                    await this.store.dispatch('markStepComplete', {
                        step: this.store.state.apply.currentRouteStep,
                        completed: true,
                    })
                    this.trackFormSubmit(valid)

                    await this.nextRoute()

                    this.busy = false
                }
            } else {
                this.busy = false
                this.trackFormSubmit(valid)
            }
        },
        trackFormSubmit(valid) {
            this.trackEvent('step_submit', {
                stepName: this.store.getters.stepViaRouteName(this.route.name)?.key,
                sectionTitle: this.store.getters.journeyInfo.currentSection?.name,
                validationPassed: valid,
            })
        },
        nextRoute() {
            if (this.store.state.apply.backToStep) {
                this.router.push({ name: this.store.state.apply.backToStep.route.name })
                this.store.state.apply.backToStep = null
                return
            }

            const nextStepInOrder = this.store.getters.getStepAfter(this.store.state.apply.currentRouteStep)
            const firstUncompletedStep = this.store.getters.firstUncompletedStep

            let nextStep = nextStepInOrder

            if (!nextStep) {
                nextStep = firstUncompletedStep
            } else if (
                (nextStepInOrder != firstUncompletedStep &&
                    firstUncompletedStep.index !== null &&
                    nextStepInOrder.index !== null &&
                    firstUncompletedStep.index < nextStepInOrder.index) ||
                this.store.getters.hasFullSummaryData ||
                (this.store.getters.hasSmallSummaryData && nextStep.section.index == 0)
            ) {
                nextStep = this.store.getters.firstUncompletedStep
            }

            if (!nextStep) {
                console.warn('No next step found')
                return
            }

            this.router.push({ name: nextStep.route.name })
        },
        bindEvents: function () {
            this.emitter.on('elementClicked', async payload => {
                const { elementName, elementType } = payload
                const vParams = this.extractVParams(this.route.fullPath)

                const eventData = {
                    campaign_code: window.CAMPAIGN_CODE,
                    campaign_integration: window.CAMPAIGN_INTEGRATION,
                    territory: process.env.TERRITORY,
                    journeyID: this.store.state.apply.journey?.id,
                    sectionTitle: this.store.getters.journeyInfo.currentSection?.name,
                    stepName: this.store.getters.stepViaRouteName(this.route.name)?.key,
                    elementType: elementType,
                    elementName: elementName,
                }

                if (vParams.length > 0) {
                    for (let i = 0; i < vParams.length; i++) {
                        eventData['v' + (i + 1)] = vParams[i]
                    }
                }

                this.trackEvent('form_element_click', eventData)
            })

            this.emitter.on('stepLoaded', async payload => {
                await this.store.dispatch('setCurrentRouteStep', this.route)

                this.setStepTitle()
                this.setTooltipText()

                this.$nextTick(() => {
                    this.trackEvent('pageview', {
                        route: this.route.fullPath,
                    })

                    const vParams = this.extractVParams(this.route.fullPath)

                    const eventData = {
                        campaign_code: window.CAMPAIGN_CODE,
                        campaign_integration: window.CAMPAIGN_INTEGRATION,
                        territory: process.env.TERRITORY,
                        journeyID: this.store.state.apply.journey?.id,
                        sectionTitle: this.store.getters.journeyInfo.currentSection?.name,
                        stepName: this.store.getters.stepViaRouteName(this.route.name)?.key,
                    }

                    if (vParams.length > 0) {
                        for (let i = 0; i < vParams.length; i++) {
                            eventData['v' + (i + 1)] = vParams[i]
                        }
                    }

                    this.trackEvent('form_step', eventData)

                    this.errorMessage = null

                    const invalidate =
                        payload?.dontInvalidate === undefined
                            ? !this.store.getters.hasAnySummaryData
                            : !payload.dontInvalidate

                    if (invalidate) {
                        this.$nextTick(() => {
                            // When navigating to a section, invalidate all next steps
                            this.store.dispatch('invalidateFutureSteps', this.route.fullPath)
                        })
                    }

                    this.busy = false
                })
            })

            this.emitter.on('nextStep', () => {
                this.$nextTick(() => {
                    this.submit()
                })
            })

            this.emitter.on('nextRoute', () => {
                this.$nextTick(() => {
                    this.nextRoute()
                })
            })
        },
        submitForm: async function () {
            this.fieldErrors = {}

            this.trackEvent('event', {
                eventCategory: 'form',
                eventAction: 'click',
                eventLabel: 'get-my-results',
            })

            try {
                const response = await submitApplication(this.store.getters.allFieldValues)

                await this.store.dispatch('clearFieldErrors')

                const data = response.data
                const redirectURL = data.redirect_url

                if (data.declined_url) {
                    this.trackFormSubmissions()
                    window.location.replace(data.declined_url)
                } else if (redirectURL === '') {
                    // Core validation message
                    this.errorMessage = await this.coreValidationMessage(data)
                    if (!this.errorMessage) {
                        this.trackFormSubmissions()
                    }

                    this.busy = false
                    this.nextRoute()
                } else {
                    const regex = /result\/([a-f0-9]{32})$/
                    const matches = regex.exec(redirectURL)
                    if (matches !== null) {
                        const ref = matches[1]

                        this.store.commit('addResultReference', ref)

                        this.trackFormSubmissions()

                        this.$router.push({
                            name: 'result',
                            params: {
                                reference: ref,
                            },
                        })
                    } else {
                        if (data.message) {
                            this.errorMessage = data.message
                        } else {
                            this.errorMessage = `Unknown error`
                        }

                        // log fail to get to result page
                        errorNotify('Failed to navigate to result page.' + this.errorMessage, 'error', 'Get Results', {
                            response,
                        })

                        this.busy = false
                    }
                }
            } catch (error) {
                // Laravel returns 422 when Request validation fails
                if (error.response && error.response.status === 422 && error.response.data) {
                    const data = error.response.data

                    // things didn't validate
                    this.errorMessage = this.translate('errors.fieldServerError')

                    if (data.errors) {
                        forEach(data.errors, async (fieldErrors, fieldName) => {
                            await this.store.dispatch('setFieldError', {
                                name: fieldName,
                                message: fieldErrors.join(' '),
                            })
                        })
                    }
                } else if (error?.response?.data?.message) {
                    this.errorMessage = error.response.data.message
                } else {
                    this.errorMessage = error
                }

                errorNotify(this.errorMessage, 'error', 'Get Results', { error })

                this.busy = false
            }
        },
        async coreValidationMessage(data) {
            if (data.error.code === '0') {
                if (data.error && data.error.desc) {
                    const fieldError = this.findCoreValidationErrors(data.error.desc)

                    if (fieldError && fieldError.fieldName && fieldError.fieldMessage) {
                        await this.store.dispatch('setFieldError', {
                            name: fieldError.fieldName,
                            message: fieldError.fieldMessage,
                        })
                        errorNotify(data.error.desc, 'error', 'CS validation error', { data })
                        return this.translate('errors.fieldServerError')
                    } else {
                        errorNotify('Unhandled core field error', 'error', 'Get Results', { data })

                        return data.error.desc.replace(/[\[]/g, ' ').replace(/[\]]/g, '')
                    }
                } else if (data.message) {
                    return data.message
                }
            } else if (data.message) {
                return data.message
            }

            return `Unknown error`
        },
        findCoreValidationErrors(error) {
            // Core seems to return 1 field at a time
            let regex = /^(Parameter)\[(.*?)\] (.*)$/
            let matches = regex.exec(error)
            if (matches !== null) {
                return {
                    fieldName: matches[2],
                    fieldMessage: `${matches[1]}  ${matches[3]}`,
                }
            }

            // "array" error
            regex = /(Parameter)(\[.*\]) (.*\.)/
            matches = regex.exec(error)
            if (matches !== null) {
                const fieldsString = matches[1]
                const message = `These fields ${matches[2]}`

                // use first field to attach message for many fields
                regex = /\[(.*?)\]/
                matches = regex.exec(fieldsString)
                if (matches !== null) {
                    return {
                        fieldName: `${matches[1]}  ${matches[2]}`,
                        fieldMessage: message,
                    }
                }
            }

            return null
        },
        trimSpacers: function () {
            const fields = this.store.state.apply.currentRouteStep?.fields || []
            for (const key in fields) {
                if ('trimSpacers' in fields[key] && fields[key].trimSpacers === true) {
                    this.store.dispatch('setFieldValue', {
                        name: key,
                        value: fields[key].value?.trim(),
                    })
                }
            }
        },
        setStepTitle: function () {
            if (!this.route?.meta?.title) {
                return false
            }

            if (typeof this.route.meta.title === 'function') {
                this.title = this.route.meta.title(this.store)
                return
            }

            this.title = this.translate(this.route.meta.title)
        },
        setTooltipText: function () {
            let tooltip = null
            if (this.store.getters.fieldValue('loanAmount') <= 1000) {
                tooltip = this.route.meta.tooltip
            } else {
                tooltip = this.route.meta.altTooltip || this.route.meta.tooltip
            }

            if (typeof tooltip === 'function') {
                this.tooltipText = tooltip()
                return
            }

            this.tooltipText = tooltip ? this.translate(tooltip) : null
        },
        trackFormSubmissions() {
            this.trackEvent('FormValidationPassed', {
                territory: process.env.TERRITORY,
                journeyID: this.store.state.apply.journey?.id,
                sectionTitle: this.store.getters.journeyInfo.currentSection?.name,
                stepName: this.store.getters.stepViaRouteName(this.route.name)?.key,
            })
        },
        // use global $t in this file to prevent weird error on mobile devices
        translate() {
            return this.store.state.apply.$t(...arguments)
        },
    },
    computed: {
        isLoanDetailsAmountPage: function () {
            return this.route.fullPath.includes('/apply/loan-details/amount')
        },
        displayTooltipWithCustomInfoBlock: function () {
            return this.tooltip && this.route?.meta?.forceDisplayTooltip
        },
        additionalInfo: function () {
            return this.translate('form.sections.aboutYou.name.additionalInfo') || null
        },
        additionalInfoHeading: function () {
            return this.translate('form.sections.aboutYou.name.additionalInfoHeading') || null
        },
        subText: function () {
            if (this.forcedUpdate) {
                this.forcedUpdate = false
            }

            if (this.route?.meta?.subText) {
                let subText = this.route?.meta?.subText
                if (typeof subText === 'function') {
                    subText = this.route?.meta?.subText(this.store)
                }

                if (!subText) {
                    return false
                }

                if (!isEmpty(this.subTextObjects)) {
                    return this.translate(subText, this.subTextObjects)
                }

                return this.translate(subText)
            }

            return false
        },
        tooltip: function () {
            return (this.route.meta && this.tooltipText) || false
        },
        customInfoBlocksEnabled: function () {
            return !!this.store.state.global.data.custom_info_boxes_enabled
        },
        customTooltip: function () {
            return this.route.meta.customTooltip
        },
        tooltipType: function () {
            return (this.route.meta && this.route.meta.tooltipType) || false
        },
        hideContinue: function () {
            return this.store.state.apply.currentRouteStep?.hideContinue
        },
        layoutType: function () {
            return (this.route.meta && this.route.meta.layoutType) || 'regular'
        },
        willSubmitResults: function () {
            return (
                this.store.getters.allFieldStepsComplete &&
                this.store.state.apply.currentRouteStep?.route?.name == 'apply.terms'
            )
        },
        continueText: function () {
            return this.willSubmitResults
                ? this.translate('form.general.getResults')
                : this.translate('form.general.continue')
        },
        legalFooter: function () {
            return this.store.state.global.data.legal_footer || false
        },
        isVervent: function () {
            return process.env.IS_VERVENT
        },
        showLegal: function () {
            return this.isVervent && this.willSubmitResults
        },
        legalNotice: function () {
            return this.store.state.global.page.legal_notice ?? this.legalFooter
        },
        isFullHeightPage: function () {
            return !this.store.state.global.data.apply_page_footer_shift
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/settings/responsive';
@import '~scss/mixins/rem';

.step-wrapper {
    background: var(--wizard-background-color);
    padding: 20px 0 50px 0px;
    text-align: center;

    &.full-height {
        min-height: calc(100vh - 55px);

        @media screen and (min-width: $bp-large) {
            min-height: calc(100vh - 100px);
        }
    }

    .sub-heading {
        color: var(--input-text-color);
    }

    .slot {
        margin: 0 auto;
        max-width: 400px;
        width: 100%;

        &.large {
            max-width: 600px;
        }
    }

    .continue-button {
        margin-top: 12px;

        @media screen and (min-width: $bp-medium) {
            margin-top: 20px;
        }
    }

    .legal {
        margin-top: 16px;
        word-break: break-word;
        font-size: rem(14px);

        @media screen and (min-width: $bp-medium) {
            margin-top: 50px;
        }
    }
}

.footer-shift {
    padding-bottom: 0;
    min-height: 600px;
}

.error-wrapper {
    top: 0;

    @media screen and (min-width: $bp-medium) {
        top: 5px;
    }

    .error {
        max-width: 320px;
        margin: 20px auto 0 auto;
    }
}
</style>

<style lang="scss">
@import '~scss/settings/responsive';

@media screen and (max-width: $bp-large) {
    .form-container {
        max-width: 320px;
        margin: 0 auto;
    }
}
</style>
